import { MunicipalityService, CustomerMunicipality, CustomerMunicipalityType, Municipality } from './municipality-service';
import { autoinject, bindable, observable, NewInstance } from 'aurelia-framework';
import { some } from 'lodash';
import { ValidationController, ValidationRules } from 'aurelia-validation';
import { ResourceService } from '../../utils/resourceService';
import { AutocompleteItem } from '../../components/autocomplete/autocomplete-service';
import { ResourcedNotificationService, NotificationType } from "../../components/notifications/notification-service";
import { ResponseHandler } from '../../utils/response-handler';

@autoinject
export class CustomerMunicipalityList {
	@bindable fiscalYearId: string;
	loaded = false;
	adding = false;
	autocompleteHasFocus = false;
	nameErrors = [];

	municipalities: CustomerMunicipality[] = [];
	customerMunicipalityTypes: CustomerMunicipalityType[];

	@observable availableMunicipalities: Municipality[];

	@bindable newMunicipalityId: string;
	@bindable newMunicipalityType: number;

	constructor(
		private municipalityService: MunicipalityService,
		private resourceService: ResourceService,
		private notificationService: ResourcedNotificationService,
		private validationController: ValidationController,
		private responseHandler: ResponseHandler) {

		ValidationRules
			.ensure((c: CustomerMunicipalityList) => c.newMunicipalityId)
				.required()
				.withMessageKey('CustomerMunicipality_ValidationError_Id_Required')
			.ensure((c: CustomerMunicipalityList) => c.newMunicipalityType)
				.required()
				.withMessageKey('CustomerMunicipality_ValidationError_Type_Required')
			.on(this);
	}

	get hasItems() {
		return this.municipalities.length > 0;
	}

	attached() {
		this.municipalityService.getMunicipalitiesForFiscalYear(this.fiscalYearId)
			.then(municipalities => {
				this.municipalities = municipalities;
				this.loaded = true;
			});

		this.municipalityService.getCustomerMunicipalityTypes()
			.then(types => this.customerMunicipalityTypes = types);

		this.municipalityService.getMunicipalities()
			.then(municipalities => this.availableMunicipalities = municipalities);
	}

	showAdd() {
		this.adding = true;
		this.autocompleteHasFocus = true;
	}

	add() {
		this.validationController.validate({ object: this })
			.then(result => {
				if (!result.valid) {
					return;
				}

				this.municipalityService.addMunicipalityForFiscalYear(
					this.fiscalYearId,
						{
							municipalityId: this.newMunicipalityId,
							municipalityType: this.newMunicipalityType
						})
					.then(response => {
						if (this.responseHandler.isSuccess(response.status)) {
							response.json().then(municipality => {
								this.municipalities.push(municipality);
								this.adding = false;
								this.newMunicipalityId = null;
								this.newMunicipalityType = null;
							});
						}
						else {
							response.json().then(error => {
								this.notificationService.push(error.message, NotificationType.Error);
							});
						}
					});
			});
	}

	cancelAdd() {
		this.adding = false;
	}

	remove(municipality: CustomerMunicipality, index: number) {
		this.municipalityService.removeMunicipalityFromFiscalYear(this.fiscalYearId, municipality)
			.then(response => {
				if (response.ok) {
					this.municipalities.splice(index, 1);
				}
				else {
					this.notificationService.push("CustomerMunicipalityList_Error_CouldNotRemoveCustomerMunicipality", NotificationType.Error);
				}
			});
	}

	filterMunicipality(item: AutocompleteItem) {
		const isMunicipalityAttachedToCustomer =
			(municipality: AutocompleteItem) => {
				return some(
					this.municipalities,
					customerMunicipality => {
						return customerMunicipality.name === municipality.name;
					}
				);
			};

		return !isMunicipalityAttachedToCustomer(item);
	}
}
